import { useEffect, useRef, useState } from "react";
import { LogOut, User as UserIcon } from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { LocaleSwitcher } from "@/i18n/locale-switcher";
import { cn } from "@/lib/utils";

interface UserMenuProps {
  /**
   * When true the footer collapses to an avatar-only trigger so it
   * fits the 64px sidebar rail. The dropdown panel itself widens
   * past the trigger so the email + Keluar action stay legible.
   */
  compact?: boolean;
}

/**
 * Bottom-left sidebar footer: avatar + user identity + dropdown
 * with logout. Matches Claude Console layout where account
 * controls live at the bottom of the navigation rail.
 */
export function UserMenu({ compact = false }: UserMenuProps = {}) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  if (!user) return null;

  const initials = (user.full_name || user.email)
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        title={compact ? user.full_name || user.email : undefined}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={compact ? user.full_name || user.email : undefined}
        className={cn(
          "flex w-full items-center gap-2 rounded-md text-left text-sm transition-colors hover:bg-ocean-100",
          isOpen && "bg-ocean-100",
          compact ? "justify-center p-2" : "px-2 py-2"
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ocean-200 text-xs font-medium text-ocean-800">
          {initials || <UserIcon size={14} />}
        </div>
        {!compact && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ocean-800">
              {user.full_name || user.email}
            </div>
            <div className="truncate text-xs text-neutral-500">{user.email}</div>
          </div>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          className={cn(
            "absolute bottom-full mb-1 rounded-md border border-ocean-100 bg-white py-1 shadow-sm",
            compact ? "left-0 min-w-[200px]" : "left-0 right-0"
          )}
        >
          <LocaleSwitcher onSelect={() => setIsOpen(false)} />
          <div className="my-1 border-t border-ocean-100" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              void logout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ocean-800 transition-colors hover:bg-ocean-50"
          >
            <LogOut size={14} className="text-ocean-400" />
            {t("navigation.userMenu.logout")}
          </button>
        </div>
      )}
    </div>
  );
}
